'use client';

import cx from 'classnames';
import { geoPath } from 'd3-geo';
import { memo, useEffect, useMemo, useState } from 'react';

import { GeolocationArea, GeolocationBoundaries } from '../../../domains/Geolocation/Geolocation';
import {
    loadWorldCountries,
    loadWorldLakes,
    WorldCountry,
    WorldLake,
} from '../../../shared/geo/geoAsset';

import {
    bboxIntersects,
    boundariesToGeometry,
    boundingBoxToBounds,
    buildRegionProjection,
    computeVisibleWindow,
    findRegionCountry,
    geometryBounds,
    RegionGeometry,
    ringsToMultiPolygon,
} from './geometry';

import type { BBox } from '../../../shared/geo/geoAsset';

import style from './RegionShapeMap.module.css';

const DEFAULT_PADDING_RATIO = 0.4;
const NO_SIBLING_REGIONS: GeolocationBoundaries[] = [];

type Backdrop = {
    countries: WorldCountry[];
    lakes: WorldLake[];
};

export type RegionShapeMapProps = {
    region: GeolocationBoundaries;
    siblingRegions?: GeolocationBoundaries[];
    /** SVG viewport width. */
    width: number;
    /** SVG viewport height; defaults to `width` for a square map. Set it for a landscape frame. */
    height?: number;
    /**
     * Frame the map on this whole area (e.g. a destination country) instead of the region
     * geometry, keeping the region highlighted within it. Defaults to region framing.
     * A box straddling the antimeridian is ignored and the map falls back to region framing.
     */
    fitBoundingBox?: GeolocationArea['boundingBox'];
    /** Margin around the framed target (region or country), as a ratio of its span. */
    paddingRatio?: number;
    className?: string;
};

function isNonEmptyPath(path: string | null): path is string {
    return path !== null && path.length > 0;
}

function buildBackdrop({
    backdrop,
    regionGeometry,
    viewWindow,
    pathFn,
}: {
    backdrop: Backdrop | null;
    regionGeometry: RegionGeometry;
    viewWindow: BBox;
    pathFn: ReturnType<typeof geoPath>;
}): { neighbourPaths: string[]; countryPath: string | null; lakePaths: string[] } {
    if (backdrop === null) {
        return { neighbourPaths: [], countryPath: null, lakePaths: [] };
    }

    const currentCountry = findRegionCountry(backdrop.countries, regionGeometry);

    const neighbourPaths = backdrop.countries
        .filter((country) => {
            return country !== currentCountry && bboxIntersects(country.bbox, viewWindow);
        })
        .map((country) => {
            return pathFn(ringsToMultiPolygon(country.rings));
        })
        .filter(isNonEmptyPath);

    const countryPath = currentCountry ? pathFn(ringsToMultiPolygon(currentCountry.rings)) : null;

    const lakePaths = backdrop.lakes
        .filter((lake) => {
            return bboxIntersects(lake.bbox, viewWindow);
        })
        .map((lake) => {
            return pathFn(ringsToMultiPolygon(lake.rings));
        })
        .filter(isNonEmptyPath);

    return { neighbourPaths, countryPath, lakePaths };
}

function RegionShapeMapComponent({
    region,
    siblingRegions = NO_SIBLING_REGIONS,
    width,
    height = width,
    fitBoundingBox,
    paddingRatio = DEFAULT_PADDING_RATIO,
    className,
}: RegionShapeMapProps) {
    const [backdrop, setBackdrop] = useState<Backdrop | null>(null);

    useEffect(() => {
        if (region.coordinates.length === 0) {
            return;
        }

        let cancelled = false;
        void Promise.all([loadWorldCountries(), loadWorldLakes()]).then(([countries, lakes]) => {
            if (!cancelled) {
                setBackdrop({ countries, lakes });
            }
        });

        return () => {
            cancelled = true;
        };
    }, [region]);

    const paths = useMemo(() => {
        if (region.coordinates.length === 0) {
            return {
                regionPath: null,
                siblingPaths: [],
                neighbourPaths: [],
                countryPath: null,
                lakePaths: [],
            };
        }

        const regionGeometry = boundariesToGeometry(region);
        // One framing pipeline for both modes: fit the target (country extent or region
        // geometry), then cull against the viewport's exact visible window. A bounding box
        // spanning the antimeridian returns null, falling back to region framing rather than a
        // globe-wide (NaN-projecting) frame.
        const countryBounds = fitBoundingBox ? boundingBoxToBounds(fitBoundingBox) : null;
        const bounds = countryBounds ?? geometryBounds(regionGeometry);
        const projection = buildRegionProjection({ bounds, width, height, paddingRatio });
        const pathFn = geoPath(projection);

        const viewWindow = computeVisibleWindow(projection, { width, height });

        const siblingPaths = siblingRegions
            .map((boundaries) => {
                return pathFn(boundariesToGeometry(boundaries));
            })
            .filter(isNonEmptyPath);

        return {
            regionPath: pathFn(regionGeometry),
            siblingPaths,
            ...buildBackdrop({
                backdrop,
                regionGeometry,
                viewWindow,
                pathFn,
            }),
        };
    }, [region, siblingRegions, width, height, fitBoundingBox, paddingRatio, backdrop]);

    if (paths.regionPath === null || paths.regionPath.length === 0) {
        return null;
    }

    return (
        <svg
            className={cx(style.map, className)}
            viewBox={`0 0 ${width} ${height}`}
            data-testid="region-shape-map"
            aria-hidden
        >
            {paths.neighbourPaths.map((path, index) => {
                return (
                    <path
                        key={`neighbour-${index}`}
                        d={path}
                        className={style.neighbour}
                        data-testid="region-shape-neighbour"
                    />
                );
            })}
            {paths.countryPath !== null ? (
                <path
                    d={paths.countryPath}
                    className={style.country}
                    data-testid="region-shape-country"
                />
            ) : null}
            {paths.lakePaths.map((path, index) => {
                return (
                    <path
                        key={`lake-${index}`}
                        d={path}
                        className={style.lake}
                        data-testid="region-shape-lake"
                    />
                );
            })}
            {paths.siblingPaths.map((path, index) => {
                return (
                    <path
                        key={`sibling-${index}`}
                        d={path}
                        className={style.sibling}
                        data-testid="region-shape-sibling"
                    />
                );
            })}
            <path
                d={paths.regionPath}
                className={style.region}
                data-testid="region-shape-highlight"
            />
        </svg>
    );
}

export const RegionShapeMap = memo(RegionShapeMapComponent);
